// src/components/lists/BeingsList/ClientPetsList.tsx
// Specialized component for Client's Pets page (http://localhost:3000/clients/5/pets)

import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box } from '@mui/material';
import {
  WarningAmberOutlined as WarningIcon,
  Pets as PetsBreedIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../../theme/theme';
import {
  BeingsListSecondaryButton,
  BeingsListTertiaryButton,
} from '../../common/Buttons';

// Import shared components and utilities
import {
  ListContainer,
  ContactDetailsSection as BaseContactDetailsSection,
  BeingRow,
  RowClickableArea,
  BeingNameSection,
  BeingIcon,
  BeingName,
  SkeletonBeingName,
  ActionButtons,
  PetCountDisplay,
  ReservationsSection,
  ReservationsList,
  ReservationItem,
  ReservationDate,
  isBeingDataLoaded,
  ContactSection,
  ContactItem,
  ItemWarningIcon,
  StillOwnedIcon,
} from './beingsListStyles';

import {
  useBeingsListState,
  useBeingsListUtils,
  getPetDetails,
  getClientLastBookings,
  createKeyDownHandler,
  getAccessibilityProps,
} from './beingsListBase';

import { ClientPetsListProps } from './beingsListTypes';
import { getCustomIcon } from '../../../utils/customIcons';
import { BookingListItem } from '../../../services/api';
import { formatBookingDateRangeString } from '../BookingsList/bookingsListUtils';

// Enhanced BeingRow with hover state for pet count display
const EnhancedBeingRow = styled(BeingRow)<{
  isOpen?: boolean;
  isEven?: boolean;
}>`
  &:hover {
    .pet-count-display {
      opacity: ${(props) => (props.isOpen ? 1 : 0.8)} !important;
    }
  }
`;

// Create a custom version for pets with different padding
const PetsContactDetailsSection = styled(BaseContactDetailsSection)({
  '@media (min-width: 600px)': {
    paddingLeft: '3rem',
    paddingRight: '3rem',
  },
});

// Styled booking row for better alignment with dates
const BookingRow = styled(ReservationItem)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(28, 46, 74, 0.3)'
        : theme.palette.grey[500],
  },
}));

const BookingDateRange = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  justifyContent: 'space-between',
});

const ClientPetsList: React.FC<ClientPetsListProps> = ({
  items,
  client,
  onEdit,
  onView,
  onDelete,
  onNewBooking,
  onViewBookings,
  onViewAdmin,
  onViewMedical,
  onOwnerClick,
  onPetClick,
}) => {
  const navigate = useNavigate();
  const { openItemId, setOpenItemId } = useBeingsListState();
  const utils = useBeingsListUtils(openItemId, setOpenItemId);

  // Bookings cache for the client - load once per client (not once ever).
  // The previous version's ref-guard never reset once set, so a later
  // change to the `client` prop (e.g. a parent page swapping which client
  // is being viewed without a full remount) would silently keep showing
  // the original client's stale bookings. Tracking the previously-fetched
  // client id lets the effect re-arm the guard specifically when the
  // client identity changes, while still fetching only once per client.
  const [clientBookings, setClientBookings] = useState<BookingListItem[]>([]);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);
  const loadedForClientIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!client?.id) {
      return;
    }

    if (loadedForClientIdRef.current === client.id) {
      return;
    }

    loadedForClientIdRef.current = client.id;
    setIsLoadingBookings(true);

    getClientLastBookings(client.id)
      .then((bookings) => {
        setClientBookings(bookings);
      })
      .catch((error) => {
        console.error('Failed to load bookings:', error);
        setClientBookings([]);
      })
      .finally(() => {
        setIsLoadingBookings(false);
      });
  }, [client?.id]);

  return (
    <ListContainer>
      {items.map((pet, index) => {
        const itemId = pet.id;
        const isOpen = openItemId === itemId;
        const isEven = index % 2 === 0;
        const petName = utils.renderName(pet);
        const petDetails = getPetDetails(pet);
        const dataLoaded = isBeingDataLoaded(pet, 'pets');

        return (
          <EnhancedBeingRow
            key={itemId}
            isOpen={isOpen}
            isEven={isEven}
            {...getAccessibilityProps(isOpen, petName)}
            onKeyDown={createKeyDownHandler(
              isOpen,
              itemId,
              utils.handleNameClick,
              utils.handleRowClick
            )}
          >
            <RowClickableArea
              isOpen={isOpen}
              onClick={() => utils.handleRowClick(itemId)}
              aria-hidden="true"
            />

            <BeingNameSection
              className="being-name-section"
              isOpen={isOpen}
              onClick={
                isOpen
                  ? (e: React.MouseEvent) => utils.handleNameClick(itemId, e)
                  : (e: React.MouseEvent) => {
                      e.stopPropagation();
                      utils.handleRowClick(itemId);
                    }
              }
            >
              <BeingIcon isOpen={isOpen}>
                {getCustomIcon(pet.petType?.type, '2rem')}
              </BeingIcon>
              {pet.showWarning && (
                <ItemWarningIcon isOpen={isOpen}>
                  <WarningIcon />
                </ItemWarningIcon>
              )}
              <StillOwnedIcon isOpen={isOpen}>
                {!pet.stillOwned && getCustomIcon('cross', '2rem')}
              </StillOwnedIcon>
              <BeingName isOpen={isOpen}>
                {dataLoaded ? (
                  <>
                    <span className="petname">{pet.name}</span>
                    <PetCountDisplay
                      isOpen={isOpen}
                      className="pet-count-display"
                    >
                      {petDetails.type} / {petDetails.breed}
                    </PetCountDisplay>
                  </>
                ) : (
                  <SkeletonBeingName
                    isOpen={isOpen}
                    widthVariant="short"
                    animation="wave"
                  />
                )}
              </BeingName>
            </BeingNameSection>

            {/* Client Pet-specific action buttons */}
            {isOpen && (
              <ActionButtons>
                <BeingsListSecondaryButton
                  minWidth="200px"
                  onClick={() => onViewBookings?.(itemId)}
                >
                  BOOKINGS
                </BeingsListSecondaryButton>

                <BeingsListTertiaryButton
                  minWidth="150px"
                  onClick={() => onView?.(itemId)}
                >
                  VIEW
                </BeingsListTertiaryButton>

                <BeingsListTertiaryButton
                  minWidth="150px"
                  onClick={() => onEdit?.(itemId)}
                >
                  EDIT
                </BeingsListTertiaryButton>
              </ActionButtons>
            )}

            {/* Client's Recent Bookings Section - Shows client's bookings, not pet-specific */}
            <ReservationsSection isOpen={isOpen}>
              <ReservationsList>
                {isLoadingBookings ? (
                  <ReservationItem>
                    <ReservationDate>Loading bookings...</ReservationDate>
                  </ReservationItem>
                ) : clientBookings.length === 0 ? (
                  <ReservationItem>
                    <ReservationDate>No bookings</ReservationDate>
                  </ReservationItem>
                ) : (
                  clientBookings.map((booking) => (
                    <BookingRow
                      key={booking.id}
                      onClick={() => navigate(`/booking/${booking.id}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/booking/${booking.id}`);
                        }
                      }}
                      aria-label={`View booking: ${formatBookingDateRangeString(booking)}`}
                    >
                      <BookingDateRange>
                        <ReservationDate>
                          {formatBookingDateRangeString(booking)}
                        </ReservationDate>
                      </BookingDateRange>
                    </BookingRow>
                  ))
                )}
              </ReservationsList>
            </ReservationsSection>

            <PetsContactDetailsSection isOpen={isOpen}>
              <ContactSection>
                {pet.showWarning && (
                  <ContactItem>
                    <WarningIcon />
                    <Typography variant="body1" fontWeight={600}>
                      {pet.notes}
                    </Typography>
                  </ContactItem>
                )}

                <ContactItem>
                  <PetsBreedIcon
                    sx={{ fontSize: '1.4rem', color: colors.primary.main }}
                  />
                  <Typography variant="body1" fontWeight={600}>
                    {pet.petBreed?.breed}
                  </Typography>
                </ContactItem>
              </ContactSection>
            </PetsContactDetailsSection>
          </EnhancedBeingRow>
        );
      })}
    </ListContainer>
  );
};

export default ClientPetsList;
